import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, XCircle, ShieldAlert, Info } from "lucide-react";

interface ErrorDialogState {
  open: boolean;
  title: string;
  message: string;
  type: "error" | "warning" | "info" | "permission";
}

interface ErrorDialogContextType {
  showError: (title: string, message: string) => void;
  showWarning: (title: string, message: string) => void;
  showPermissionError: (message?: string) => void;
  showFromApiError: (title: string, err: any) => void;
}

function humanizeFieldName(field: string): string {
  return field
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function flattenFieldErrors(errors: any): string[] {
  if (!errors || typeof errors !== "object") return [];
  const out: string[] = [];
  const fieldErrors = errors.fieldErrors;
  if (fieldErrors && typeof fieldErrors === "object") {
    for (const [field, msgs] of Object.entries(fieldErrors)) {
      const list = Array.isArray(msgs) ? msgs : [msgs];
      for (const m of list) {
        if (typeof m === "string" && m.trim()) {
          out.push(`${humanizeFieldName(field)}: ${m}`);
        }
      }
    }
  }
  const formErrors = errors.formErrors;
  if (Array.isArray(formErrors)) {
    for (const m of formErrors) {
      if (typeof m === "string" && m.trim()) out.push(m);
    }
  }
  return out;
}

function extractErrorMessage(err: any): string {
  const raw = err?.message || String(err);
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try {
      const parsed = JSON.parse(jsonMatch[0]);
      const baseMessage = parsed.message || parsed.error || "";
      const details = flattenFieldErrors(parsed.errors);
      if (details.length > 0) {
        // If the top-level message is generic ("Invalid request",
        // "Validation failed", etc.), prefer just the field details.
        const generic = /^(invalid request|validation failed|bad request)\.?$/i.test(baseMessage.trim());
        return generic ? details.join("\n") : `${baseMessage}\n\n${details.join("\n")}`;
      }
      return baseMessage || raw;
    } catch {}
  }
  const codeMatch = raw.match(/^\d{3}:\s*(.+)$/);
  if (codeMatch) {
    return codeMatch[1].trim();
  }
  return raw;
}

function getStatusInfo(rawMessage: string): { title: string; message: string; type: ErrorDialogState["type"] } {
  const statusMatch = rawMessage.match(/^(\d{3})/);
  const code = statusMatch ? parseInt(statusMatch[1]) : 0;
  const cleanMessage = extractErrorMessage({ message: rawMessage });

  if (code === 400) {
    return { title: "Invalid Input", message: cleanMessage || "Please check your input and try again.", type: "warning" };
  }
  if (code === 401) {
    return { title: "Session Expired", message: cleanMessage || "Your session has expired. Please log in again.", type: "permission" };
  }
  if (code === 403) {
    return { title: "Access Denied", message: cleanMessage || "You don't have permission for this action.", type: "permission" };
  }
  if (code === 404) {
    return { title: "Not Found", message: cleanMessage || "The item you're looking for could not be found.", type: "warning" };
  }
  if (code === 409) {
    return { title: "Conflict", message: cleanMessage || "This action conflicts with existing data.", type: "warning" };
  }
  if (code === 429) {
    return { title: "Too Many Requests", message: cleanMessage || "Please wait a moment and try again.", type: "warning" };
  }
  if (code >= 500) {
    return { title: "Something Went Wrong", message: cleanMessage || "An unexpected error occurred. Please try again.", type: "error" };
  }

  return { title: "Error", message: cleanMessage || "An unexpected error occurred.", type: "error" };
}

const ErrorDialogContext = createContext<ErrorDialogContextType | null>(null);

export function ErrorDialogProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ErrorDialogState>({
    open: false,
    title: "",
    message: "",
    type: "error",
  });

  const showError = useCallback((title: string, message: string) => {
    setState({ open: true, title, message, type: "error" });
  }, []);

  const showWarning = useCallback((title: string, message: string) => {
    setState({ open: true, title, message, type: "warning" });
  }, []);

  const showPermissionError = useCallback((message?: string) => {
    setState({
      open: true,
      title: "Access Denied",
      message: message || "You don't have permission for this action.",
      type: "permission",
    });
  }, []);

  const showFromApiError = useCallback((title: string, err: any) => {
    const raw = err?.message || String(err);
    const info = getStatusInfo(raw);
    setState({
      open: true,
      title: title || info.title,
      message: info.message,
      type: info.type,
    });
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const iconMap = {
    error: <XCircle className="w-10 h-10 text-red-400" />,
    warning: <AlertTriangle className="w-10 h-10 text-amber-400" />,
    info: <Info className="w-10 h-10 text-cyan-400" />,
    permission: <ShieldAlert className="w-10 h-10 text-orange-400" />,
  };

  const buttonColorMap = {
    error: "bg-red-600 hover:bg-red-700",
    warning: "bg-amber-600 hover:bg-amber-700",
    info: "bg-cyan-600 hover:bg-cyan-700",
    permission: "bg-orange-600 hover:bg-orange-700",
  };

  return (
    <ErrorDialogContext.Provider value={{ showError, showWarning, showPermissionError, showFromApiError }}>
      {children}
      <Dialog open={state.open} onOpenChange={(open) => !open && close()}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-md" data-testid="error-dialog">
          <DialogHeader className="items-center text-center gap-3">
            {iconMap[state.type]}
            <DialogTitle className="text-white text-lg" data-testid="error-dialog-title">
              {state.title}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-sm leading-relaxed whitespace-pre-line" data-testid="error-dialog-message">
              {state.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center mt-2">
            <Button
              onClick={close}
              className={`${buttonColorMap[state.type]} text-white min-w-[120px]`}
              data-testid="error-dialog-ok"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ErrorDialogContext.Provider>
  );
}

export function useErrorDialog(): ErrorDialogContextType {
  const ctx = useContext(ErrorDialogContext);
  if (!ctx) throw new Error("useErrorDialog must be used within ErrorDialogProvider");
  return ctx;
}
